import React from "react";
import { Link } from "react-router-dom";
import portfolio from "../data/portfolio.json";
import Section from "../components/portfolio/Section";
import {
  MailIcon,
  GithubIcon,
  CodeforcesIcon,
  LinkedinIcon,
  ScholarIcon,
} from "../components/portfolio/Icons";
import { renderInlineBold } from "../utils/inlineMarkdown";

/**
 * The landing page. Renders entirely from the committed portfolio.json — no API
 * call, so the first page a visitor hits paints with zero round-trips.
 */

// Authored, not extracted: a compression of the first clause of `summary`.
const TAGLINE =
  "Ph.D. in Computer Science (Computer Vision) — real-time perception systems, end to end.";

// Referees' names and emails are third-party personal data. Off by default on a
// public page — set true only with their agreement. Even when true, emails stay
// hidden; publishing those is a further deliberate edit.
const SHOW_REFERENCES = false;

function Hero({ data }) {
  const { contact } = data;
  return (
    <header className="pt-16">
      <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
        {data.name}
      </h1>
      {/* No max-width: the tagline is meant to sit on one line at full width.
          It still wraps naturally on narrow screens rather than overflowing. */}
      <p className="mt-4 text-lg text-slate-600">{TAGLINE}</p>

      <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <a
          className="inline-flex items-center gap-2 text-slate-600 transition hover:text-indigo-600"
          href={`mailto:${contact.email}`}
        >
          <MailIcon /> {contact.email}
        </a>
        <a
          className="inline-flex items-center gap-2 text-slate-600 transition hover:text-indigo-600"
          href={contact.githubUrl}
          target="_blank"
          rel="noreferrer"
        >
          <GithubIcon /> {contact.github}
        </a>
        {contact.codeforces && (
          <a
            className="inline-flex items-center gap-2 text-slate-600 transition hover:text-indigo-600"
            href={contact.codeforcesUrl}
            target="_blank"
            rel="noreferrer"
          >
            <CodeforcesIcon /> {contact.codeforces}
          </a>
        )}
        {contact.linkedin && (
          <a
            className="inline-flex items-center gap-2 text-slate-600 transition hover:text-indigo-600"
            href={contact.linkedinUrl}
            target="_blank"
            rel="noreferrer"
          >
            <LinkedinIcon /> {contact.linkedin}
          </a>
        )}
        {contact.scholar && (
          <a
            className="inline-flex items-center gap-2 text-slate-600 transition hover:text-indigo-600"
            href={contact.scholarUrl}
            target="_blank"
            rel="noreferrer"
          >
            <ScholarIcon /> {contact.scholar}
          </a>
        )}
      </div>
    </header>
  );
}

function Summary({ text }) {
  return (
    <Section title="Summary">
      {/* Justified, with hyphenation on — without it the last line of each
          justified row stretches into visible word gaps. */}
      <p className="hyphens-auto text-justify text-sm leading-relaxed text-slate-600">
        {renderInlineBold(text)}
      </p>
    </Section>
  );
}

function Skills({ skills }) {
  return (
    <Section title="Skills">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {Object.entries(skills).map(([name, body]) => (
          <div
            key={name}
            className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-indigo-300 hover:shadow-md"
          >
            <h3 className="text-sm font-semibold text-slate-900">{name}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Experience({ items }) {
  return (
    <Section title="Work & Project Experience">
      <ol className="relative border-l border-slate-200 pl-6">
        {items.map((org, i) => (
          <li key={i} className="mb-10 last:mb-0">
            <span className="absolute -left-[5px] mt-2 h-2.5 w-2.5 rounded-full bg-indigo-500" />
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <h3 className="text-base font-semibold text-slate-900">
                {org.org}
              </h3>
              <span className="text-sm text-slate-400">{org.location}</span>
            </div>
            {org.projects.map((p, j) => (
              <div key={j} className="mt-4">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <p className="text-sm font-medium italic text-slate-700">
                    {p.title}
                  </p>
                  <span className="text-sm text-slate-400">{p.period}</span>
                </div>
                <ul className="mt-2 list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-600 marker:text-slate-300">
                  {p.bullets.map((b, k) => (
                    <li key={k}>{renderInlineBold(b)}</li>
                  ))}
                </ul>
                {p.links && (
                  <div className="mt-2 flex flex-wrap gap-x-4 pl-5">
                    {p.links.map((l, k) => (
                      <a
                        key={k}
                        href={l.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-indigo-600 transition hover:text-indigo-700 hover:underline"
                      >
                        {l.label} ↗
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </li>
        ))}
      </ol>
    </Section>
  );
}

function Education({ items }) {
  return (
    <Section title="Education">
      <div className="space-y-6">
        {items.map((e, i) => (
          <div key={i}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <h3 className="text-base font-semibold text-slate-900">
                {e.school}
              </h3>
              <span className="text-sm text-slate-400">{e.location}</span>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <p className="text-sm italic text-slate-700">{e.degree}</p>
              <span className="text-sm text-slate-400">{e.period}</span>
            </div>
            {e.notes && (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-slate-600 marker:text-slate-300">
                {e.notes.map((n, j) => (
                  <li key={j}>{renderInlineBold(n)}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function Publications({ items }) {
  return (
    <Section title="Publications & Patents">
      <ol className="list-decimal space-y-3 pl-5 text-sm leading-relaxed text-slate-600 marker:text-slate-300">
        {items.map((p, i) => (
          <li key={i}>
            {renderInlineBold(p.text)}
            {p.url && (
              <a
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className="ml-2 whitespace-nowrap text-xs text-indigo-600 transition hover:text-indigo-700 hover:underline"
              >
                {p.urlLabel || "document"} ↗
              </a>
            )}
          </li>
        ))}
      </ol>
    </Section>
  );
}

function Achievements({ items }) {
  return (
    <Section title="Achievements">
      <div className="space-y-4">
        {items.map((row, i) => (
          <div key={i} className="flex gap-6">
            <span className="w-12 shrink-0 text-sm font-semibold text-indigo-600">
              {row.year}
            </span>
            <div className="space-y-1 text-sm leading-relaxed text-slate-600">
              {row.items.map((it, j) => (
                <p key={j}>
                  {renderInlineBold(it.text)}
                  {it.url && (
                    <a
                      href={it.url}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-2 whitespace-nowrap text-xs text-indigo-600 transition hover:text-indigo-700 hover:underline"
                    >
                      certificate ↗
                    </a>
                  )}
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function References({ items }) {
  return (
    <Section title="References">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {items.map((r, i) => (
          <div
            key={i}
            className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-600 shadow-sm"
          >
            <p className="font-semibold text-slate-900">{r.name}</p>
            <p>{r.affiliation}</p>
            <p>{r.institution}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

export default function PortfolioPage() {
  return (
    <div className="min-h-screen bg-white">
      <main className="mx-auto max-w-5xl px-4 pb-24">
        <Hero data={portfolio} />
        {portfolio.summary && <Summary text={portfolio.summary} />}
        {portfolio.skills && <Skills skills={portfolio.skills} />}
        <Experience items={portfolio.experience} />
        <Education items={portfolio.education} />
        <Publications items={portfolio.publications} />
        <Achievements items={portfolio.achievements} />
        {SHOW_REFERENCES && <References items={portfolio.references} />}

        <footer className="mt-20 border-t border-slate-200 pt-8 text-sm text-slate-500">
          Writing on computer vision, LLMs and systems lives in the{" "}
          <Link to="/articles" className="text-indigo-600 hover:underline">
            Knowledge Base
          </Link>
          .
        </footer>
      </main>
    </div>
  );
}
